import { InteractionStatus } from '@azure/msal-browser';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useState } from 'react';

import { loginRequest } from './config';

/**
 * Session lifecycle: automatic sign-in on arrival, and the sign-out that must not undo it.
 *
 * Opening the dashboard redirects straight to Entra without showing a button. Everyone who
 * reaches this origin is a Torrens account holder already signed in to Office 365, so the
 * button was a click between them and an SSO bounce that needs no credentials.
 *
 * The cost of automatic redirect is that a misconfiguration becomes a loop the user cannot
 * read, and an infinite redirect is the one failure mode that leaves no way to diagnose
 * itself — the page never renders, so there is nothing on screen to explain it and nowhere to
 * put a retry. Two separate guards keep that from happening, because they defend against
 * different things:
 *
 * `redirectStarted` is module state, and covers one page load. It stops a re-render, a
 * StrictMode double-effect, or a second component from firing a second redirect while the
 * first is still being built.
 *
 * `ATTEMPTED` is sessionStorage, and covers the loop across page loads. It is set before
 * leaving for Entra and cleared once an account is in hand, so finding it still set on
 * arrival means the last attempt came back with nothing. At that point we stop redirecting
 * and render the reason with a manual retry, which costs one wasted round trip and makes the
 * failure legible instead of invisible.
 *
 * Both live in sessionStorage rather than localStorage, matching `msalConfig`'s cache: they
 * describe this tab's session and should die with it. A sign-out flag in localStorage would
 * follow the user into an unrelated later visit, and a stale failure flag would outlive the
 * app registration fix that resolved it.
 */

const ATTEMPTED = 'ira.sign-in-attempted';
const SIGNED_OUT = 'ira.signed-out';

let redirectStarted = false;
let redirectFailure: string | null = null;

/**
 * Records an Entra-side refusal seen while processing the redirect back.
 *
 * Called from the bootstrap, which is the only place that sees it: `handleRedirectPromise()`
 * rejects for consent, conditional access and reply-URL problems, and those are answers from
 * the directory rather than a broken build. Treating them as fatal would replace the whole
 * app with an error page carrying an AADSTS code and no way forward, so they are handed to
 * the sign-in screen to show alongside a retry.
 */
export function noteRedirectFailure(error: unknown): void {
  redirectFailure = describe(error);
}

/**
 * Signs out and stops the automatic redirect from immediately signing back in.
 *
 * Without the flag, automatic sign-in turns sign-out into a no-op: the logout redirect lands
 * back on the origin, the gate sees no account and sends the user straight to Entra, which
 * still has their Office 365 session and returns them signed in.
 *
 * Redirect rather than popup, because a popup logout leaves the SPA holding a cached account
 * whose session is gone, which then fails silently on the next token acquisition.
 */
export function signOut(instance: IPublicClientApplication): void {
  sessionStorage.setItem(SIGNED_OUT, '1');
  sessionStorage.removeItem(ATTEMPTED);
  void instance.logoutRedirect();
}

export interface AutoSignIn {
  /**
   * Why the automatic redirect was withheld, or null while it is under way. Non-null is the
   * only state in which the sign-in screen is worth rendering.
   */
  blocked: string | null;
  /** Retry by hand, from the screen shown when `blocked` is set. */
  signIn: () => void;
}

export function useAutoSignIn(): AutoSignIn {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const [blocked, setBlocked] = useState<string | null>(null);

  const signIn = useCallback(() => {
    sessionStorage.removeItem(SIGNED_OUT);
    sessionStorage.setItem(ATTEMPTED, '1');
    redirectStarted = true;
    redirectFailure = null;
    setBlocked(null);
    instance.loginRedirect(loginRequest).catch((error: unknown) => {
      redirectStarted = false;
      setBlocked(describe(error));
    });
  }, [instance]);

  useEffect(() => {
    if (isAuthenticated) {
      // Reaching here is the only proof the round trip worked, so it is the only place the
      // loop guard may be released.
      sessionStorage.removeItem(ATTEMPTED);
      sessionStorage.removeItem(SIGNED_OUT);
      return;
    }
    if (inProgress !== InteractionStatus.None || redirectStarted || blocked !== null) return;

    if (redirectFailure) {
      setBlocked(redirectFailure);
    } else if (sessionStorage.getItem(SIGNED_OUT)) {
      setBlocked('signed-out');
    } else if (sessionStorage.getItem(ATTEMPTED)) {
      setBlocked('no-session');
    } else {
      signIn();
    }
  }, [isAuthenticated, inProgress, blocked, signIn]);

  return { blocked, signIn };
}

function describe(error: unknown): string {
  // MSAL errors carry the AADSTS text on `errorMessage`, and their `message` is a terser
  // wrapper that often omits the part naming the actual problem.
  if (error && typeof error === 'object' && 'errorMessage' in error) {
    const msal = error as { errorCode?: string; errorMessage?: string };
    const parts = [msal.errorCode, msal.errorMessage].filter(Boolean);
    if (parts.length) return parts.join(': ');
  }
  return error instanceof Error ? error.message : String(error);
}
